import type { EventRsvpConfirmationBody } from "@anggunpraya-templates/base/app/schemas/event";
import { useEventGuestStore } from "../stores/event-guest";

/**
 * API kecil yang diekspos oleh komponen form RSVP anak.
 *
 * Saat ini dipakai untuk membersihkan state validasi ketika pilihan attendance
 * berubah dan field yang aktif ikut berganti.
 */
interface RsvpFormExpose {
  clear: () => void;
}

/**
 * Composable shared untuk flow RSVP activity.
 *
 * File ini sengaja besar karena di sinilah policy RSVP disatukan:
 * - status hadir / tidak hadir
 * - pilihan activity
 * - edit attendance
 * - simulasi preview
 * - submit ke API
 *
 * @returns State, derived state, dan action yang dibutuhkan section RSVP activity
 *
 * @example
 * ```typescript
 * const {
 *   state,
 *   loading,
 *   chooseAttending,
 *   chooseNotAttending,
 *   submit,
 * } = useRsvpActivityForm();
 * ```
 */
export function useRsvpActivityForm() {
  const { $services } = useNuxtApp();
  const toastNotification = useToastNotification();
  const { flavor } = useRuntimeConfig().public;
  const { loadEventGuest } = useEventGuestLoader();

  const eventGuestStore = useEventGuestStore();
  const { eventGuest } = storeToRefs(eventGuestStore);

  const { loading, setLoading, resetLoading } = useLoadingControl<"submit">();

  const formRef = useTemplateRef<RsvpFormExpose>("formRef");
  const { isEventPreview } = useInvitationContext();

  const isEditing = ref(false);
  const selectedRsvpActivity = ref<string | null>(null);
  const state = ref<EventRsvpConfirmationBody>({
    status: "unconfirmed",
    rsvp_activities: [],
  });

  /** Guest dianggap "sudah confirm" saat status final ada dan user tidak sedang edit ulang. */
  const isAlreadyConfirmed = computed(() => {
    if (!eventGuest.value) {
      return false;
    }

    return ["attending", "not-attending"].includes(eventGuest.value.status) && !isEditing.value;
  });

  /** Opsi activity RSVP yang siap dirender, termasuk opsi "both" bila diperlukan. */
  const eventRsvpActivityItems = computed<{
    label: string;
    value: string;
  }[]>(() => {
    if (!eventGuest.value) {
      return [];
    }

    const actualItems = eventGuest.value.event_guest_rsvp_activity.map(activity => ({
      value: activity.egra_id,
      label: activity.rsvp_activity.name,
    }));

    if (actualItems.length < 2) {
      return actualItems;
    }

    return [
      ...actualItems,
      {
        value: "both",
        label: "Keduanya",
      },
    ];
  });

  /** Form field hanya dirender untuk activity yang benar-benar dipilih saat attending. */
  const renderedFormFields = computed(() => {
    if (!state.value.rsvp_activities.length || state.value.status !== "attending") {
      return [];
    }

    return state.value.rsvp_activities;
  });

  const isPreviewOrPortfolio = computed(() => {
    return ["preview", "portfolio"].includes(flavor);
  });

  /** Sinkronkan state form dengan guest aktif agar sumber data tetap tunggal. */
  watch(
    () => eventGuest.value,
    (guest) => {
      if (!guest) {
        state.value = {
          status: "unconfirmed",
          rsvp_activities: [],
        };
        selectedRsvpActivity.value = null;
        isEditing.value = false;
        return;
      }

      state.value = {
        status: guest.status,
        rsvp_activities: guest.event_guest_rsvp_activity.map((event) => {
          const { egra_id, pax, rsvp_status, rsvp_activity } = event;

          return {
            egra_id,
            pax,
            rsvp_status,
            rsvp_activity: {
              name: rsvp_activity.name,
            },
          };
        }),
      };
    },
    {
      immediate: true,
    },
  );

  /** Shortcut untuk mengubah status RSVP menjadi hadir. */
  function chooseAttending() {
    state.value.status = "attending";
  }

  /** Shortcut untuk mengubah status RSVP menjadi tidak hadir. */
  function chooseNotAttending() {
    state.value.status = "not-attending";
    selectedRsvpActivity.value = null;
  }

  /**
   * Mengubah daftar `rsvp_activities` aktif berdasarkan pilihan radio/list UI.
   * Function ini sengaja menjaga transform payload tetap keluar dari komponen render.
   *
   * @param event Event change dari input pilihan activity RSVP
   */
  function onSelectedRsvpChange(event: Event) {
    formRef.value?.clear();

    const target = event.target as HTMLInputElement;
    const value = target.value;

    if (value === "both") {
      state.value.rsvp_activities = eventGuest.value?.event_guest_rsvp_activity.map(activity => ({
        egra_id: activity.egra_id,
        pax: 0,
        rsvp_status: "attending",
        rsvp_activity: {
          name: activity.rsvp_activity.name,
        },
      })) || [];

      return;
    }

    const selectedActivity = eventGuest.value?.event_guest_rsvp_activity.find(activity => activity.egra_id === value);

    if (!selectedActivity) {
      return;
    }

    state.value.rsvp_activities = eventGuest.value?.event_guest_rsvp_activity.map((activity) => {
      if (activity.egra_id === selectedActivity.egra_id) {
        return {
          egra_id: activity.egra_id,
          pax: 0,
          rsvp_status: "attending",
          rsvp_activity: {
            name: activity.rsvp_activity.name,
          },
        };
      }

      return {
        egra_id: activity.egra_id,
        pax: 0,
        rsvp_status: "pending",
        rsvp_activity: {
          name: activity.rsvp_activity.name,
        },
      };
    }) || [];
  }

  /**
   * Mengembalikan form ke mode edit setelah guest pernah melakukan konfirmasi.
   *
   * @returns Promise yang selesai setelah validasi form dibersihkan ulang
   */
  async function editAttendance() {
    state.value = {
      status: "unconfirmed",
      rsvp_activities: state.value.rsvp_activities.map(item => ({
        ...item,
        rsvp_status: "pending",
        pax: 0,
      })),
    };

    selectedRsvpActivity.value = null;
    isEditing.value = true;

    await nextTick(() => {
      formRef.value?.clear();
    });
  }

  /**
   * Submit RSVP ke API atau ke mode simulasi.
   *
   * Setelah submit sukses di mode nyata, guest akan dimuat ulang
   * agar section confirmed membaca data server terbaru.
   *
   * @returns Promise yang selesai setelah submit RSVP diproses
   */
  async function submit() {
    try {
      setLoading("submit");

      if (state.value.status === "unconfirmed") {
        toastNotification.add("warning", {
          title: "Peringatan",
          description: "Silakan pilih status kehadiran terlebih dahulu.",
        });

        return;
      }

      if (!eventGuest.value) {
        toastNotification.add("error", {
          title: "Gagal",
          description: "Data tamu tidak ditemukan.",
        });

        return;
      }

      const previewMode = isPreviewOrPortfolio.value || isEventPreview.value;

      if (previewMode) {
        await new Promise(resolve => setTimeout(resolve, 1500));

        selectedRsvpActivity.value = null;
        state.value = {
          status: "unconfirmed",
          rsvp_activities: state.value.rsvp_activities.map(item => ({
            ...item,
            pax: 0,
          })),
        };
      }
      else {
        const body: EventRsvpConfirmationBody = {
          ...state.value,
          rsvp_activities: state.value.rsvp_activities.map((activity) => {
            if (["pending", "not-attending"].includes(activity.rsvp_status)) {
              return {
                ...activity,
                rsvp_status: "not-attending",
                pax: 0,
              };
            }

            return {
              ...activity,
            };
          }),
        };

        await $services.event.rsvpConfirmation(
          eventGuest.value.event_id,
          eventGuest.value.event_guest_id,
          body,
        );

        await loadEventGuest(eventGuest.value.code);
      }

      toastNotification.add("success", {
        title: `Berhasil${previewMode ? " (Simulasi)" : ""}`,
        description: "Terima kasih atas konfirmasi kehadiran Anda.",
      });
    }
    catch (error) {
      if (error instanceof Error) {
        const message = error.message || "Terjadi kesalahan saat mengirim konfirmasi.";

        console.error(message);
        toastNotification.add("error", {
          title: "Gagal",
          description: message,
        });
      }
    }
    finally {
      resetLoading();

      if (isEditing.value) {
        isEditing.value = false;
      }
    }
  }

  return {
    formRef,
    loading,
    selectedRsvpActivity,
    state,
    isAlreadyConfirmed,
    eventRsvpActivityItems,
    renderedFormFields,
    chooseAttending,
    chooseNotAttending,
    onSelectedRsvpChange,
    editAttendance,
    submit,
  };
}
